/**
 * Репозиторий сущности «Задача».
 *
 * CRUD, мягкое удаление и запись в очередь синхронизации — в базовой фабрике
 * (`src/shared/db/repository.ts`). Здесь добавлены значения по умолчанию для
 * полей повторения: подавляющее большинство задач разовые, и требовать шесть
 * дополнительных полей при каждом создании значило бы заставлять весь код
 * помнить о механике, которая ему не нужна.
 */

import { db } from '@/shared/db/database';
import { type CreateInput, createRepository } from '@/shared/db/repository';
import type { Task } from '@/shared/db/schema';

const base = createRepository<Task>('tasks', db.tasks);

/** Поля, которые подставляются сами, если их не передали. */
type DefaultedField =
  | 'recurrence'
  | 'recurrenceInterval'
  | 'recurrenceWeekdays'
  | 'recurrenceFrom'
  | 'recurrenceUntil'
  | 'seriesId'
  | 'externalId'
  | 'externalCalendarId'
  | 'syncedHash'
  | 'reminderId'
  | 'reminderListId'
  | 'reminderHash';

export type TaskCreateInput = Omit<CreateInput<Task>, DefaultedField> &
  Partial<Pick<Task, DefaultedField>>;

const DEFAULTS: Pick<Task, DefaultedField> = {
  recurrence: 'none',
  recurrenceInterval: 1,
  recurrenceWeekdays: 0,
  recurrenceFrom: 'planned',
  recurrenceUntil: null,
  seriesId: null,

  /*
    Связь с календарём устройства заводится сверкой, а не тем, кто создаёт
    задачу. Требовать три `null` от каждого места, где рождается задача, —
    значит заставлять весь код помнить о механике, которая его не касается.
  */
  externalId: null,
  externalCalendarId: null,
  syncedHash: null,
  reminderId: null,
  reminderListId: null,
  reminderHash: null,
};

function withDefaults(input: TaskCreateInput): CreateInput<Task> {
  return { ...DEFAULTS, ...input };
}

export const tasksRepo = {
  ...base,
  create: (input: TaskCreateInput) => base.create(withDefaults(input)),
  createMany: (inputs: TaskCreateInput[]) => base.createMany(inputs.map(withDefaults)),
};
